import { type FormEvent, type JSX, useCallback, useEffect, useState } from 'react';

import {
  aendern,
  ARTNAME,
  eigenschaftAnlegen,
  eigenschaftEntfernen,
  EIGENSCHAFTSARTEN,
  EIGENSCHAFTSARTNAME,
  konfiguration as ladeKonfiguration,
  KREISE,
  sperrzeitAnlegen,
  sperrzeitEntfernen,
  SPERRARTEN,
  SPERRARTNAME,
  statusname,
  statusZurArt,
  VERTRAGSSTANDNAME,
  zugangEinrichten,
  ZUGEHOERIGKEITSNAME,
  type Eigenschaftsart,
  type Ressourcenkonfiguration as Konfiguration,
  type Ressourcenzeile,
  type Sperrart,
} from '../api/ressourcen';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Abschnitt, Feldraster, Kennzahlenleiste, type Kennzahl } from '../komponenten/Akte';
import { Trainervertrag } from './Trainervertrag';
import { Abrechnungsgrundlage } from './Abrechnungsgrundlage';

/**
 * Was an einer Ressource einzustellen ist (RES-01 bis RES-03, RES-15).
 *
 * ============================================================================
 * DIE LÜCKE, DIE SIE SCHLIESST
 * ============================================================================
 *
 * Bis hierher ließ sich eine Ressource ANLEGEN und danach nicht mehr ändern.
 * Kein Umbenennen, keine Platzzahl, kein Status — wer sich vertippt hatte,
 * musste das Einspielskript noch einmal laufen lassen. Und alles, was RES-02
 * (Qualifikationen) und RES-15 (Sperrzeiten) verlangen, stand als Tabelle in
 * der Datenbank und war von keiner Maske erreichbar.
 *
 * Das ist kein Schönheitsfehler: `fach.ressource_frei` — die eine Auskunft,
 * auf der Vorschlagsliste, Doppelbelegungsschutz und die automatische
 * Zuteilung nach TAG-82 aufsetzen — liest genau diese Tabellen.
 *
 * ============================================================================
 * DREI ABSCHNITTE, EINE RESSOURCE
 * ============================================================================
 *
 * Stammdaten, Eigenschaften, Sperrzeiten. Sie stehen untereinander und nicht
 * hinter Reitern: Wer ein Modul einrichtet, geht sie der Reihe nach durch,
 * und ein Reiter, den man nicht anklickt, wird nicht gepflegt. Ungepflegtes
 * ist hier der teure Fall — ein Modul ohne Eignung schlägt die Planung nicht
 * vor, ein Trainer ohne Qualifikation deckt keinen Bedarf.
 *
 * Bis ÄA-15 waren es fünf. Regelverfügbarkeit (RES-14) und Gruppen (RES-04)
 * sind entfallen: Der Betriebsrahmen steht am Standort und geht über den
 * Betriebstag in die Planung, und was Gleichartiges zusammenfasst, ist die
 * Eigenschaft — sie pflegt sich mit der Ressource statt daneben.
 *
 * ============================================================================
 * DIE STAMMDATEN HABEN EINEN SPEICHERKNOPF, DIE LISTEN NICHT
 * ============================================================================
 *
 * Ein Formular mit acht Feldern will als Ganzes abgeschickt werden — sonst
 * springt der Blitz nach jedem Tastendruck. Eine Liste dagegen wird Zeile für
 * Zeile ergänzt und geleert; ein Speicherknopf darüber wäre die Frage „gilt
 * das jetzt schon?" bei jeder einzelnen Zeile.
 *
 * Deshalb auch kein Stift je Feld wie in der Kundenakte (ÄA-41): Kennung,
 * Belegung und Platzzahl hängen aneinander (RES-03), und ein Feld, das allein
 * sichert, schickte eine Platzzahl ohne die Belegung, die sie erlaubt.
 *
 * ============================================================================
 * DIE AKTENANSICHT (ÄA-41, Auftrag #19)
 * ============================================================================
 *
 * Kopf mit Kennung und Bezeichnung, darunter die Kennzahlenleiste — was an
 * dieser Ressource gerade gilt, in einem Blick: Art, Standort, Status,
 * Zugehörigkeit, ablaufende Nachweise und anstehende Sperrzeiten. Ein Nachweis,
 * der abläuft, und eine fehlende Vereinbarung stehen dort als Frist in der
 * Achtungsfarbe. Darunter die aufklappbaren Abschnitte, die Stammdaten als
 * Feldraster. Die Abschnitte bleiben untereinander und werden keine Reiter —
 * der Grund steht oben.
 */

/**
 * Ob diese Ressource ein MENSCH ist (RES-01, ÄA-21).
 *
 * ============================================================================
 * DREI FELDER HÄNGEN DARAN (13.09.2026)
 * ============================================================================
 *
 * Auftraggeber: „Belegung raus, da immer nur einzeln … verantwortliche Rolle
 * raus. Bei Sperrzeit art nur Abwesenheit und sonstiges."
 *
 * Alle drei folgen aus derselben Sache: Ein Mensch ist zu einer Zeit an einer
 * Stelle, er wird nicht gewartet, und für ihn ist niemand „verantwortlich" im
 * Sinne von RES-21 — er sagt selbst ab (RES-33). Die Felder verschwinden
 * deshalb nicht wegen des MENÜPUNKTS, unter dem man sie geöffnet hat, sondern
 * wegen der ART der Ressource: Ein Trainer bleibt einer, auch wenn man ihn über
 * die Suche aufmacht.
 *
 * `KREISE.trainer` und keine eigene Liste: Wer eine Personenart ergänzt, trägt
 * sie dort ein, und diese Stelle folgt.
 */
function istMensch(art: string): boolean {
  return (KREISE.trainer as readonly string[]).includes(art);
}

/** Die Bezeichnung einer Sperrart — sie steht hier, damit sie überall gleich lautet. */
function sperrart(art: string): string {
  return SPERRARTNAME[art as Sperrart] ?? art;
}

/**
 * Welche Sperrarten zur Wahl stehen (RES-15, 13.09.2026).
 *
 * Auftraggeber: „Bei Sperrzeit art nur Abwesenheit und sonstiges." Und das
 * stimmt mit der Sache überein: „Wartung" und „Feiertag" sagt man über eine
 * Gleitfläche, „anderweitig gebunden" über ein Fahrzeug, das beim Händler
 * steht. Ein Mensch ist abwesend — oder es hat einen Grund, den nur er kennt.
 *
 * DIE DATENBANK KENNT WEITERHIN ALLE FÜNF. Das ist Absicht: Eine Sperrzeit, die
 * vor dieser Änderung als „Wartung" an einem Trainer stand, bleibt lesbar
 * (`sperrart()` benennt sie), sie lässt sich nur nicht mehr neu anlegen. Eine
 * Bedingung in der Datenbank hätte den Bestand abgewiesen, ohne dass jemand
 * ihn gesehen hätte.
 */
function sperrartenZurArt(art: string): readonly Sperrart[] {
  return istMensch(art) ? (['abwesenheit', 'sonstiges'] as const) : SPERRARTEN;
}

function eigenschaftsart(art: string): string {
  return EIGENSCHAFTSARTNAME[art as Eigenschaftsart] ?? art;
}

/** Ein Zeitpunkt als `TT.MM.JJJJ HH:MM` — der Sperrzeitraum wird gelesen, nicht gerechnet. */
function zeitpunkt(wert: string): string {
  const d = new Date(wert);
  return Number.isNaN(d.getTime())
    ? wert
    : d.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function tag(wert: string | null): string {
  if (wert === null) return '';
  const d = new Date(`${wert}T12:00:00`);
  return Number.isNaN(d.getTime()) ? wert : d.toLocaleDateString('de-DE');
}

export function Ressourcenkonfiguration({
  ressource,
  beiSchliessen,
  beiAenderung,
  darfHonorar = false,
  darfHonorarAendern = false,
  darfEingangsrechnungPruefen = false,
}: {
  readonly ressource: Ressourcenzeile;
  readonly beiSchliessen: () => void;
  /** Die Liste dahinter zeigt Kennung, Bezeichnung und Status — sie muss nachziehen. */
  readonly beiAenderung: () => void;
  /**
   * RES-73: Ohne `artikel/honorar_sehen` steht der Honorarabschnitt nicht da,
   * und die Ausfallbeträge bleiben leer. Der Vorgabewert ist `false` — was
   * Geld zeigt, zeigt es nur, wenn es ausdrücklich erlaubt ist.
   */
  readonly darfHonorar?: boolean;
  readonly darfHonorarAendern?: boolean;
  /** RES-76: `rechnung:buchen` — die Buchhaltung prüft die Eingangsrechnung. */
  readonly darfEingangsrechnungPruefen?: boolean;
}): JSX.Element {
  const [stand, setzeStand] = useState<Konfiguration | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laedt, setzeLaedt] = useState(true);

  /* Die Stammdaten als Entwurf — sie werden als Ganzes gespeichert. */
  const [entwurf, setzeEntwurf] = useState<{
    kennung: string;
    bezeichnung: string;
    standort: string;
    status: string;
    belegungsart: 'ausschliesslich' | 'plaetze';
    platzzahl: string;
    verantwortlicheRolle: string;
    gehoertZu: string;
    bemerkung: string;
    /* ÄA-21 und RES-39 — nur bei Personen sichtbar. */
    zugehoerigkeit: '' | 'intern' | 'extern';
    auftragsverarbeitung: 'entfaellt' | 'fehlt' | 'liegt_vor' | 'gekuendigt';
    zweckbindung: string;
    vereinbarungAm: string;
    /** RES-13: die weiteren Einsatzorte — bei einer Person als Kästchen. */
    einsatzorte: readonly string[];
  } | null>(null);

  const [eigenschaftsartWahl, setzeEigenschaftsart] = useState<Eigenschaftsart>('qualifikation');
  const [schluessel, setzeSchluessel] = useState('');
  const [eigenschaftsname, setzeEigenschaftsname] = useState('');
  const [gueltigBis, setzeGueltigBis] = useState('');
  /** RES-54: Mehrfachauswahl — angekreuzt wird, was auf einmal dazukommt. */
  const [gewaehlt, setzeGewaehlt] = useState<readonly string[]>([]);

  /*
   * RES-28: Der Onlinezugang. Das Kennwort steht NUR hier im Zustand dieser
   * Maske und geht mit dem Schliessen verloren — es wird nirgends abgelegt
   * (LEG-13), auch nicht im Entwurfsspeicher.
   */
  const [zugangName, setzeZugangName] = useState('');
  const [zugangMail, setzeZugangMail] = useState('');
  const [zugangKennwort, setzeZugangKennwort] = useState<string | null>(null);

  /*
   * CRM-104: Welche Rückfrage offen ist. Beide Zeilen gehen hart aus der
   * Datenbank — es gibt keinen Vermerk „entfernt", den man zurücksetzen könnte.
   */
  const [nachfrage, setzeNachfrage] = useState<
    | { readonly art: 'eigenschaft'; readonly zeile: Konfiguration['eigenschaften'][number] }
    | { readonly art: 'sperrzeit'; readonly zeile: Konfiguration['sperrzeiten'][number] }
    | null
  >(null);

  /*
   * DIE VORBELEGUNG FOLGT DER ART (13.09.2026).
   *
   * „Wartung" stand hier für jede Ressource — bei einem Trainer steht sie seit
   * heute nicht einmal mehr zur Wahl, und eine Vorbelegung, die nicht in der
   * Liste steht, zeigt ein leeres Feld. Die Auswahl setzt sie mit der Ressource
   * neu (siehe `useEffect` unten): Wer von einem Modul zu einem Trainer
   * wechselt, soll nicht „Wartung" mitnehmen.
   */
  const [sperrartWahl, setzeSperrart] = useState<Sperrart>(
    istMensch(ressource.art) ? 'abwesenheit' : 'wartung',
  );
  const [sperrgrund, setzeSperrgrund] = useState('');
  const [sperrVon, setzeSperrVon] = useState('');
  const [sperrBis, setzeSperrBis] = useState('');

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      const k = await ladeKonfiguration(ressource.id);
      setzeStand(k);
      setzeEntwurf({
        kennung: k.kennung,
        bezeichnung: k.bezeichnung,
        standort: k.standort ?? '',
        einsatzorte: k.einsatzorte,
        status: k.status,
        belegungsart: k.belegungsart,
        platzzahl: k.platzzahl === null ? '' : String(k.platzzahl),
        verantwortlicheRolle: k.verantwortlicheRolle ?? '',
        gehoertZu: k.gehoertZu ?? '',
        bemerkung: k.bemerkung ?? '',
        zugehoerigkeit: k.zugehoerigkeit ?? '',
        auftragsverarbeitung: k.auftragsverarbeitung,
        zweckbindung: k.zweckbindung ?? '',
        vereinbarungAm: k.vereinbarungAm ?? '',
      });
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [ressource.id]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /*
   * DIE SPERRART FOLGT DER RESSOURCE.
   *
   * Die Maske bleibt stehen, wenn man von einer Ressource zur naechsten wechselt
   * — der Zustand des Sperrzeitformulars auch. Ohne diese Zeile truege ein
   * Trainer die Vorbelegung „Wartung" mit, die bei ihm nicht zur Wahl steht:
   * Das Feld stuende leer, und das Anlegen schluege fehl, ohne zu sagen warum.
   */
  useEffect(() => {
    setzeSperrart(istMensch(ressource.art) ? 'abwesenheit' : 'wartung');
  }, [ressource.art]);

  /**
   * Jeder Handgriff lädt danach neu.
   *
   * Das ist teurer als das Ergebnis an Ort und Stelle einzurechnen — und
   * richtig: Eine neue Qualifikation ändert die Ablauffristen und die Liste
   * dessen, was noch angeboten wird. Wer das in der Maske nachrechnet, führt
   * dieselbe Regel zweimal.
   */
  async function handgriff(tun: () => Promise<unknown>, sagen: string): Promise<void> {
    setzeMeldung(null);
    try {
      await tun();
      await holen();
      setzeMeldung(sagen);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }

  /**
   * Der Handgriff aus der Rückfrage (CRM-104): Er fängt NICHT. Der Fehler
   * steht im Pop-up, das offen bleibt — und nicht ein zweites Mal oben auf
   * der Seite.
   */
  async function bestaetigt(tun: () => Promise<unknown>, sagen: string): Promise<void> {
    setzeMeldung(null);
    await tun();
    setzeNachfrage(null);
    await holen();
    setzeMeldung(sagen);
  }

  async function stammSpeichern(ereignis: FormEvent): Promise<void> {
    ereignis.preventDefault();
    if (entwurf === null) return;
    await handgriff(async () => {
      await aendern(ressource.id, {
        kennung: entwurf.kennung.trim(),
        bezeichnung: entwurf.bezeichnung.trim(),
        standort: entwurf.standort === '' ? null : entwurf.standort,
        einsatzorte: entwurf.einsatzorte,
        status: entwurf.status,
        belegungsart: entwurf.belegungsart,
        /*
         * RES-03: Die Platzzahl gehört zur Platzbelegung und nur dorthin. Wer
         * auf „ausschließlich" stellt, schickt ausdrücklich `null` — sonst
         * bliebe die alte Zahl stehen, und die Datenbank wiese ab.
         */
        platzzahl:
          entwurf.belegungsart === 'plaetze' ? Number(entwurf.platzzahl || '0') || null : null,
        verantwortlicheRolle:
          entwurf.verantwortlicheRolle === '' ? null : entwurf.verantwortlicheRolle,
        gehoertZu: entwurf.gehoertZu === '' ? null : entwurf.gehoertZu,
        bemerkung: entwurf.bemerkung.trim() === '' ? null : entwurf.bemerkung.trim(),
        /*
         * ÄA-21, RES-39: Die vier Felder gehen nur mit, wenn die Ressource eine
         * PERSON ist. Bei einem Raum wiese die Datenbank sie ab — und zwar zu
         * Recht: Ein Raum ist weder intern noch extern.
         */
        ...(entwurf.zugehoerigkeit === ''
          ? {}
          : {
              zugehoerigkeit: entwurf.zugehoerigkeit,
              auftragsverarbeitung:
                entwurf.zugehoerigkeit === 'extern' ? entwurf.auftragsverarbeitung : 'entfaellt',
              zweckbindung: entwurf.zweckbindung.trim() === '' ? null : entwurf.zweckbindung.trim(),
              vereinbarungAm: entwurf.vereinbarungAm === '' ? null : entwurf.vereinbarungAm,
            }),
      });
      beiAenderung();
    }, 'Die Stammdaten sind gespeichert.');
  }

  const eigenschaftEintragbar =
    eigenschaftsartWahl === 'qualifikation' ? gewaehlt.length > 0 : schluessel.trim() !== '';

  async function eigenschaftEintragen(ereignis: FormEvent): Promise<void> {
    ereignis.preventDefault();
    if (!eigenschaftEintragbar) return;
    await handgriff(async () => {
      if (eigenschaftsartWahl === 'qualifikation') {
        /*
         * NACHEINANDER und nicht nebenläufig: Jede Zeile geht durch
         * denselben Prüfweg, und bei einem Fehler soll erkennbar
         * bleiben, welche schon steht.
         */
        for (const k of gewaehlt) {
          await eigenschaftAnlegen(ressource.id, {
            art: 'qualifikation',
            schluessel: k,
            /* Die Bezeichnung kommt aus der Liste (0158). */
            bezeichnung: null,
            gueltigAb: null,
            gueltigBis: gueltigBis === '' ? null : gueltigBis,
            nachweis: null,
          });
        }
        setzeGewaehlt([]);
      } else {
        await eigenschaftAnlegen(ressource.id, {
          art: eigenschaftsartWahl,
          schluessel: schluessel.trim(),
          bezeichnung: eigenschaftsname.trim() === '' ? null : eigenschaftsname.trim(),
          gueltigAb: null,
          gueltigBis: gueltigBis === '' ? null : gueltigBis,
          nachweis: null,
        });
        setzeSchluessel('');
        setzeEigenschaftsname('');
      }
      setzeGueltigBis('');
    }, 'Die Eigenschaft ist eingetragen.');
  }

  /* RES-15: Der Grund ist Pflicht — ohne ihn geht die Sperre nicht hinaus. */
  const sperrzeitEintragbar = sperrgrund.trim() !== '' && sperrVon !== '' && sperrBis !== '';

  async function sperrzeitEintragen(ereignis: FormEvent): Promise<void> {
    ereignis.preventDefault();
    if (!sperrzeitEintragbar) return;
    await handgriff(async () => {
      await sperrzeitAnlegen(ressource.id, {
        art: sperrartWahl,
        grund: sperrgrund.trim(),
        von: new Date(sperrVon).toISOString(),
        bis: new Date(sperrBis).toISOString(),
        wiederholungTage: null,
      });
      setzeSperrgrund('');
      setzeSperrVon('');
      setzeSperrBis('');
    }, 'Die Sperrzeit ist eingetragen.');
  }

  if (stand === null || entwurf === null) {
    return (
      <div className="karte">
        <p className="hinweiszeile">{laedt ? 'Wird geladen …' : 'Nicht gefunden.'}</p>
        {fehler !== null && <Meldung fehler={fehler} />}
        <button type="button" className="knopf" data-auch-telefon onClick={beiSchliessen}>
          Schließen
        </button>
      </div>
    );
  }

  /*
   * DIE ANGEKREUZTEN STANDORTE — die Spalte und die Tabelle als EINE Menge
   * (RES-13).
   *
   * Die Reihenfolge kommt aus `stand.standorte` und nicht aus der Eingabe:
   * Sonst hinge es davon ab, in welcher Folge jemand klickt, welcher Ort beim
   * Abwählen des ersten nachrückt.
   */
  const orte = stand.standorte
    .map((o) => o.kennung)
    .filter((k) => k === entwurf.standort || entwurf.einsatzorte.includes(k));

  /**
   * Einen Haken setzen oder wegnehmen.
   *
   * Der erste der Menge wird zum `standort`, die übrigen zu `einsatzorte` —
   * und wer den Haken am verorteten Standort wegnimmt, rückt den nächsten nach.
   * Kein Haken heißt `null`, und das bedeutet seit 0062 „überall einsetzbar".
   */
  /*
   * Als Zuweisung und nicht als `function`: Eine Funktionsdeklaration wird an
   * den Anfang des Blocks gezogen — also VOR die Schranke oben —, und der
   * Übersetzer muss dort wieder mit `null` rechnen.
   */
  const setzeOrt = (kennung: string, an: boolean): void => {
    const jetzt = new Set(orte);
    if (an) jetzt.add(kennung);
    else jetzt.delete(kennung);

    const liste = stand.standorte.map((o) => o.kennung).filter((k) => jetzt.has(k));
    const haupt = liste.includes(entwurf.standort) ? entwurf.standort : (liste[0] ?? '');
    setzeEntwurf({
      ...entwurf,
      standort: haupt,
      einsatzorte: liste.filter((k) => k !== haupt),
    });
  };

  /*
   * Was noch nicht dasteht. Eine bereits eingetragene Qualifikation ein
   * zweites Mal anzubieten hieße, zur Dopplung einzuladen — und zwei Zeilen
   * mit demselben Schlüssel sind zwei Nachweise, von denen der längere gilt
   * (RES-54). Wer das will, trägt sie an der Zeile nach.
   */
  const offeneQualifikationen = stand.qualifikationen.filter(
    (q) =>
      q.aktiv
      && !stand.eigenschaften.some(
        (e) => e.art === 'qualifikation' && e.schluessel === q.schluessel,
      ),
  );

  /*
   * DIE KENNZAHLENLEISTE (ÄA-41) — aus dem GESPEICHERTEN Stand, nicht aus dem
   * Entwurf: Sie sagt, was gilt, und nicht, was gerade getippt wird.
   */
  const ortsnamen = [stand.standort, ...stand.einsatzorte]
    .filter((k): k is string => k !== null && k !== '')
    .map((k) => stand.standorte.find((o) => o.kennung === k)?.bezeichnung ?? k);
  const abgelaufen = stand.eigenschaften.filter((e) => e.abgelaufen).length;
  const laeuftAb = stand.eigenschaften.filter((e) => !e.abgelaufen && e.laeuftBaldAb).length;
  const anstehend = stand.sperrzeiten.filter((s) => !s.vorbei).length;
  const zahlen: Kennzahl[] = [
    { beschriftung: 'Art', wert: ARTNAME[stand.art] },
    {
      beschriftung: ortsnamen.length > 1 ? 'Standorte' : 'Standort',
      /* RES-13: ohne Standort ist die Ressource an jedem einsetzbar. */
      wert: ortsnamen.length === 0 ? 'jeder Standort' : ortsnamen.join(', '),
    },
    { beschriftung: 'Status', wert: statusname(stand.status, stand.art) },
    ...(stand.zugehoerigkeit === null
      ? []
      : [
          {
            beschriftung: 'Zugehörigkeit',
            wert:
              stand.zugehoerigkeit === 'extern'
                ? `${ZUGEHOERIGKEITSNAME.extern ?? 'extern'} · ${VERTRAGSSTANDNAME[stand.auftragsverarbeitung] ?? stand.auftragsverarbeitung}`
                : (ZUGEHOERIGKEITSNAME[stand.zugehoerigkeit] ?? stand.zugehoerigkeit),
            /* RES-39: Ohne Vereinbarung bleibt der Tagesplan zu — das ist zu tun. */
            frist: stand.zugehoerigkeit === 'extern' && stand.auftragsverarbeitung !== 'liegt_vor',
          },
        ]),
    {
      beschriftung: 'Nachweise',
      /* RES-54: Ein abgelaufener Nachweis deckt keinen Bedarf mehr. */
      wert:
        abgelaufen + laeuftAb === 0
          ? `${String(stand.eigenschaften.length)} eingetragen`
          : [
              abgelaufen > 0 ? `${String(abgelaufen)} abgelaufen` : '',
              laeuftAb > 0 ? `${String(laeuftAb)} ${laeuftAb === 1 ? 'läuft' : 'laufen'} bald ab` : '',
            ]
              .filter((t) => t !== '')
              .join(' · '),
      frist: abgelaufen + laeuftAb > 0,
    },
    /* RES-15 */
    { beschriftung: 'Sperrzeiten', wert: anstehend === 0 ? 'keine anstehend' : `${String(anstehend)} anstehend` },
  ];

  return (
    <div className="stapel">
      <div className="karte">
        <div className="karte__titel">
          <h2>
            {stand.kennung} — {stand.bezeichnung}
          </h2>
          <button type="button" className="knopf" data-auch-telefon onClick={beiSchliessen}>
            Schließen
          </button>
        </div>
        <Kennzahlenleiste zahlen={zahlen} />
      </div>

      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      <div className="karte tafel__abschnitte">
        {/* ==================================================================
            1. STAMMDATEN (RES-01, RES-03, RES-51, RES-52)
            ================================================================== */}
        <Abschnitt titel="Stammdaten">
          <form onSubmit={(e) => void stammSpeichern(e)}>
            <Feldraster>
              <div className="feld">
                <label htmlFor="konf-kennung">Kennung</label>
                <input
                  id="konf-kennung"
                  value={entwurf.kennung}
                  onChange={(e) => {
                    setzeEntwurf({ ...entwurf, kennung: e.target.value });
                  }}
                />
                {/*
                  Die Kennung steht auf dem Aushang — und genau deshalb muss sie sich
                  berichtigen lassen. Wer sie ändert, soll aber wissen, wo sie steht.
                */}
                <span className="feld__hilfe">
                  Der Name, unter dem das Haus die Fläche kennt („M3"). Er steht auf dem Aushang
                  und im Trainerplan.
                </span>
              </div>
              <div className="feld">
                <label htmlFor="konf-bezeichnung">Bezeichnung</label>
                <input
                  id="konf-bezeichnung"
                  value={entwurf.bezeichnung}
                  onChange={(e) => {
                    setzeEntwurf({ ...entwurf, bezeichnung: e.target.value });
                  }}
                />
              </div>
              {/*
                RES-13: „Ressourcen können mehreren Standorten zugeordnet werden."

                ============================================================
                KÄSTCHEN BEIM MENSCHEN, AUSWAHL BEIM DING (13.09.2026)
                ============================================================

                Auftraggeber: „jetzt kann für einen trainer nicht hannover und
                braunschweig als standort ausgewählt werden. baue bitte um in
                checkboxen."

                Ein Fahrmodul steht, wo es steht — dort ist eine Mehrfachwahl keine
                Freiheit, sondern eine Fehlerquelle. Ein Mensch fährt montags am
                Hauptstandort und mittwochs am zweiten Standort.

                DIE SPALTE `standort` BLEIBT und trägt weiterhin EINEN Ort: Daran
                hängen der Tagesplan im Trainerbereich (RES-32), der Filter der Liste
                (RES-17) und die Vorauswahl der Planung. Die Kästchen schreiben beides
                — der erste Haken wird zum Standort, die übrigen zu Einsatzorten. Wer
                den Haken am Standort wegnimmt, rückt den nächsten nach.
              */}
              {istMensch(ressource.art) ? (
                <div className="feld">
                  <span className="feld__beschriftung" id="konf-orte">
                    Standorte
                  </span>
                  <fieldset className="wahlgruppe" aria-labelledby="konf-orte">
                    {stand.standorte.map((o) => (
                      <label key={o.kennung} className="wahlreihe">
                        <input
                          type="checkbox"
                          checked={orte.includes(o.kennung)}
                          onChange={(e) => {
                            setzeOrt(o.kennung, e.target.checked);
                          }}
                        />
                        {o.bezeichnung}
                        {entwurf.standort === o.kennung && orte.length > 1 && (
                          <span className="marke">verortet</span>
                        )}
                      </label>
                    ))}
                  </fieldset>
                  {/*
                    OHNE HAKEN HEISST „ÜBERALL" — und das steht da.

                    So ist die Spalte seit 0062 gemeint, und die Vorschlagsliste
                    rechnet danach. Ohne diesen Satz läse sich der leere Kasten als
                    „nirgends", und das ist das Gegenteil.
                  */}
                  <span className="feld__hilfe">
                    {orte.length === 0
                      ? 'Kein Haken: überall einsetzbar — an jedem Standort des Hauses.'
                      : orte.length === 1
                        ? 'Hier wird er eingeplant. Weitere Standorte einfach ankreuzen.'
                        : `An allen ${String(orte.length)} angekreuzten Standorten einplanbar. `
                          + 'Sein Tagesplan im Trainerbereich zeigt den Standort mit der Marke '
                          + '„verortet".'}
                  </span>
                </div>
              ) : (
                <div className="feld">
                  <label htmlFor="konf-standort">Standort</label>
                  <select
                    id="konf-standort"
                    value={entwurf.standort}
                    onChange={(e) => {
                      setzeEntwurf({ ...entwurf, standort: e.target.value });
                    }}
                  >
                    <option value="">ohne Standort — überall einsetzbar</option>
                    {stand.standorte.map((s) => (
                      <option key={s.kennung} value={s.kennung}>
                        {s.bezeichnung}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="feld">
                <label htmlFor="konf-status">Status</label>
                <select
                  id="konf-status"
                  value={entwurf.status}
                  onChange={(e) => {
                    setzeEntwurf({ ...entwurf, status: e.target.value });
                  }}
                >
                  {/*
                    WAS ZUR WAHL STEHT, HÄNGT AN DER ART. „In Wartung" und „außer
                    Betrieb" sagt man über eine Gleitfläche, nicht über einen
                    Trainer (Auftraggeber, 09.09.2026); am Trainer stehen sie
                    deshalb nicht in der Liste, und die Datenbank weist sie dort
                    auch ab (0164).
                  */}
                  {statusZurArt(ressource.art).map((s) => (
                    <option key={s} value={s}>
                      {statusname(s, ressource.art)}
                    </option>
                  ))}
                </select>
                {/*
                  RES-15, RES-16: Was nicht auf „aktiv" steht, ist zu keinem
                  Zeitpunkt frei — `ressource_frei` prüft den Status VOR allen
                  Zeitfragen. Das gehört an das Feld und nicht in eine Fußnote.
                */}
                <span className="feld__hilfe">
                  Nur „aktiv" ist verplanbar; alles andere meldet die Verfügbarkeitsprüfung, bevor
                  es um Zeiten geht.{' '}
                  {ressource.art === 'trainer'
                    ? 'Wer vorübergehend nicht kann, bekommt eine Sperrzeit mit Grund — sie nennt Grund und Zeitraum, der Status nennt beides nicht.'
                    : 'Für eine befristete Unterbrechung ist die Sperrzeit das richtige Mittel.'}
                </span>
              </div>
              {/*
                BELEGUNG UND PLATZZAHL GIBT ES NUR BEI DEN ASSETS (13.09.2026).

                Auftraggeber: „Belegung raus, da immer nur einzeln." Ein Mensch steht
                zu einer Zeit an einer Stelle. Der gespeicherte Wert bleibt
                unangetastet — er lautet bei allen 46 Trainern `ausschliesslich`, und
                die Spalte ist NOT NULL (0062).
              */}
              {!istMensch(ressource.art) && (
              <div className="feld">
                <label htmlFor="konf-belegungsart">Belegung</label>
                <select
                  id="konf-belegungsart"
                  value={entwurf.belegungsart}
                  onChange={(e) => {
                    setzeEntwurf({
                      ...entwurf,
                      belegungsart: e.target.value as 'ausschliesslich' | 'plaetze',
                    });
                  }}
                >
                  <option value="ausschliesslich">ausschließlich — nie zwei zugleich</option>
                  <option value="plaetze">nach Plätzen — mehrere, solange die Zahl trägt</option>
                </select>
              </div>
              )}
              {!istMensch(ressource.art) && entwurf.belegungsart === 'plaetze' && (
                <div className="feld">
                  <label htmlFor="konf-platzzahl">Platzzahl</label>
                  <input
                    id="konf-platzzahl"
                    type="number"
                    min={1}
                    value={entwurf.platzzahl}
                    onChange={(e) => {
                      setzeEntwurf({ ...entwurf, platzzahl: e.target.value });
                    }}
                  />
                  {/* TAG-19 */}
                  <span className="feld__hilfe">
                    {stand.art === 'modul'
                      ? 'Wie viele Fahrzeuge gleichzeitig auf die Fläche passen.'
                      : 'Sie begrenzt die gleichzeitig eingeplanten Teilnehmer.'}
                  </span>
                </div>
              )}
              {/*
                DIE VERANTWORTLICHE ROLLE GIBT ES NUR BEI DEN ASSETS (13.09.2026).

                Auftraggeber: „verantwortliche Rolle raus." Sie wird bei einem Ausfall
                benachrichtigt (RES-21) — bei einem Trainer sagt er selbst ab (RES-33),
                und die Disposition erfährt es an der Anfrage. Keiner der 46 Trainer
                trug eine; es geht dabei nichts verloren.
              */}
              {!istMensch(ressource.art) && (
              <div className="feld">
                <label htmlFor="konf-rolle">Verantwortliche Rolle</label>
                <select
                  id="konf-rolle"
                  value={entwurf.verantwortlicheRolle}
                  onChange={(e) => {
                    setzeEntwurf({ ...entwurf, verantwortlicheRolle: e.target.value });
                  }}
                >
                  <option value="">keine</option>
                  {stand.rollen.map((r) => (
                    <option key={r.kennung} value={r.kennung}>
                      {r.bezeichnung}
                    </option>
                  ))}
                </select>
                {/* RES-01: eine ROLLE und keine Person — wer verantwortet, wechselt. */}
                <span className="feld__hilfe">
                  Sie wird bei einem Ausfall benachrichtigt. Eine Rolle und keine Person:
                  Wer verantwortet, wechselt — die Zuständigkeit nicht.
                </span>
              </div>
              )}
              {(stand.art === 'fahrzeug'
                || stand.art === 'betriebsmittel'
                || stand.art === 'sonstiges') && (
                <div className="feld">
                  <label htmlFor="konf-gehoertzu">Gehört zu</label>
                  <select
                    id="konf-gehoertzu"
                    value={entwurf.gehoertZu}
                    onChange={(e) => {
                      setzeEntwurf({ ...entwurf, gehoertZu: e.target.value });
                    }}
                  >
                    <option value="">frei — keinem Modul fest zugeordnet</option>
                    {stand.traeger.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.bezeichnung}
                      </option>
                    ))}
                  </select>
                  {/* RES-53 */}
                  <span className="feld__hilfe">Betriebsmittel hängen an einem Modul.</span>
                </div>
              )}
            </Feldraster>
            <div className="feld">
              <label htmlFor="konf-bemerkung">Bemerkung</label>
              <textarea
                id="konf-bemerkung"
                rows={2}
                value={entwurf.bemerkung}
                onChange={(e) => {
                  setzeEntwurf({ ...entwurf, bemerkung: e.target.value });
                }}
              />
            </div>
            <div className="knopfreihe">
              <button type="submit" className="knopf knopf--haupt">
                Stammdaten speichern
              </button>
            </div>
          </form>
        </Abschnitt>

        {/* ==================================================================
            2. EIGENSCHAFTEN UND QUALIFIKATIONEN (RES-02, RES-54)
            ================================================================== */}
        <Abschnitt titel="Eigenschaften und Qualifikationen">
          {/* TAG-14 */}
          <p className="feld__hilfe">
            {/*
              Der SCHLÜSSEL ist das, wonach ein Bedarf sucht (RES-05). Das gehört
              über die Liste: Wer hier „Fahrsicherheitstrainer PKW" als Bezeichnung
              einträgt und den Schlüssel vergisst, hat eine Zeile ohne Wirkung.
            */}
            Danach kommt eine Ressource für einen Bedarf in Frage. Gesucht wird über den{' '}
            <strong>Schlüssel</strong> — die Bezeichnung ist nur die Anzeige und darf sich ändern,
            ohne dass ein Bedarf ungedeckt wird. Ein Nachweis mit Ablaufdatum deckt nur bis dahin:
            Gerechnet wird gegen den <strong>Termintag</strong>, nicht gegen heute.
          </p>
          {stand.eigenschaften.length === 0 ? (
            <p className="hilfe">
              Nichts hinterlegt. Ein Bedarf, der eine Qualifikation verlangt, findet diese
              Ressource damit nicht.
            </p>
          ) : (
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle" aria-label="Eigenschaften und Qualifikationen">
                <thead>
                  <tr>
                    <th scope="col">Art</th>
                    <th scope="col">Schlüssel</th>
                    <th scope="col">Bezeichnung</th>
                    <th scope="col">Gültig</th>
                    <th scope="col">Handlung</th>
                  </tr>
                </thead>
                <tbody>
                  {stand.eigenschaften.map((e) => (
                    <tr key={e.id}>
                      <td>{eigenschaftsart(e.art)}</td>
                      <td>
                        <code>{e.schluessel}</code>
                      </td>
                      <td>{e.bezeichnung ?? '—'}</td>
                      <td>
                        {e.gueltigBis === null ? (
                          <span className="hilfe">ohne Ablauf</span>
                        ) : e.abgelaufen ? (
                          <span className="marke marke--krit">
                            abgelaufen am {tag(e.gueltigBis)}
                          </span>
                        ) : e.laeuftBaldAb ? (
                          <span className="marke marke--warn">läuft ab am {tag(e.gueltigBis)}</span>
                        ) : (
                          `bis ${tag(e.gueltigBis)}`
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--eingriff knopf--klein"
                          aria-label={`${e.schluessel} entfernen …`}
                          onClick={() => {
                            setzeNachfrage({ art: 'eigenschaft', zeile: e });
                          }}
                        >
                          Entfernen …
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/*
            DAS FORMULAR ZUM EINTRAGEN STEHT IM HAUSLAYOUT (Auftrag CRM-73):
            Beschriftung über dem Feld, Raster, Knopfreihe — wie die Stammdaten
            darüber. Bis dahin stand es als Filterleiste da, mit der
            Beschriftung vor dem Feld und kleinen Knöpfen.
          */}
          <form className="eintragform" onSubmit={(e) => void eigenschaftEintragen(e)}>
            <div className="eintragraster">
              <div className="feld">
                <label htmlFor="konf-eigenschaftsart">
                  Art<span className="nur-vorlesen"> der Eigenschaft</span>
                </label>
                <select
                  id="konf-eigenschaftsart"
                  value={eigenschaftsartWahl}
                  onChange={(e) => {
                    setzeEigenschaftsart(e.target.value as Eigenschaftsart);
                  }}
                >
                  {EIGENSCHAFTSARTEN.map((a) => (
                    <option key={a} value={a}>
                      {EIGENSCHAFTSARTNAME[a]}
                    </option>
                  ))}
                </select>
              </div>
              {/*
                ==================================================================
                DIE QUALIFIKATION IST EINE AUSWAHL, ALLES ANDERE BLEIBT FREITEXT
                ==================================================================

                Festlegung des Auftraggebers vom 09.09.2026: vier Werte, und ein
                Trainer kann mehrere tragen. Die Liste steht in der Datenbank
                (0158) und wird mit der Konfiguration mitgeliefert.

                MEHRERE KÄSTCHEN, MEHRERE ZEILEN — nicht ein Feld mit vier Haken.
                Jede Qualifikation bekommt ihre eigene Zeile, weil jede ihren
                eigenen Nachweis und ihr eigenes Ablaufdatum hat (RES-54). Wer
                zwei auf einmal einträgt, gibt ihnen zunächst dasselbe Datum und
                berichtigt es an der Zeile; ein gemeinsames Feld ließe das gar
                nicht mehr zu.

                Ausstattung, Sprache und Zertifikat bleiben frei: Sie sind keine
                Auswahl, und niemand hat je gesagt, welche es geben soll.

                Die Kästchen nehmen im Raster den Platz von Schlüssel und
                Anzeigename ein (zwei Spalten): „Gültig bis" bleibt beim Wechsel
                der Art an seiner Stelle.
              */}
              {eigenschaftsartWahl === 'qualifikation' ? (
                <div className="feld eintragraster__doppelt">
                  <span className="feld__beschriftung" id="konf-qualifikationen">
                    Qualifikationen
                  </span>
                  {offeneQualifikationen.length === 0 ? (
                    <p className="hilfe eintragraster__wahl">
                      {stand.qualifikationen.length === 0
                        ? 'Es ist noch keine Qualifikation eingerichtet.'
                        : 'Alle eingerichteten Qualifikationen sind bereits eingetragen.'}
                    </p>
                  ) : (
                    <fieldset
                      className="wahlgruppe eintragraster__wahl"
                      aria-labelledby="konf-qualifikationen"
                    >
                      {offeneQualifikationen.map((q) => (
                        <label key={q.schluessel} className="wahlreihe">
                          <input
                            type="checkbox"
                            checked={gewaehlt.includes(q.schluessel)}
                            onChange={(e) => {
                              setzeGewaehlt(
                                e.target.checked
                                  ? [...gewaehlt, q.schluessel]
                                  : gewaehlt.filter((k) => k !== q.schluessel),
                              );
                            }}
                          />
                          {q.bezeichnung}
                        </label>
                      ))}
                    </fieldset>
                  )}
                </div>
              ) : (
                <>
                  <div className="feld">
                    <label htmlFor="konf-schluessel">Schlüssel</label>
                    <input
                      id="konf-schluessel"
                      value={schluessel}
                      onChange={(e) => {
                        setzeSchluessel(e.target.value);
                      }}
                    />
                    {/* RES-05: Gesucht wird über den Schlüssel, Zeichen für Zeichen. */}
                    <span className="feld__hilfe">Danach sucht ein Bedarf.</span>
                  </div>
                  {/*
                    „Anzeigename" und nicht „Bezeichnung": Das Stammdatenfeld darüber
                    heißt schon so, und zwei gleich benannte Eingaben in einer Maske
                    sind für ein Vorleseprogramm dieselbe Auskunft zweimal (NFA-14).
                    Der Name trifft es hier ohnehin besser — gesucht wird über den
                    Schlüssel, angezeigt wird das hier.
                  */}
                  <div className="feld">
                    <label htmlFor="konf-anzeigename">
                      Anzeigename<span className="nur-vorlesen"> der Eigenschaft</span>
                    </label>
                    <input
                      id="konf-anzeigename"
                      value={eigenschaftsname}
                      onChange={(e) => {
                        setzeEigenschaftsname(e.target.value);
                      }}
                    />
                  </div>
                </>
              )}
              <div className="feld">
                <label htmlFor="konf-gueltigbis">Gültig bis</label>
                <input
                  id="konf-gueltigbis"
                  type="date"
                  value={gueltigBis}
                  onChange={(e) => {
                    setzeGueltigBis(e.target.value);
                  }}
                />
                <span className="feld__hilfe">Leer heißt ohne Ablauf.</span>
              </div>
            </div>
            <div className="knopfreihe">
              <button
                type="submit"
                className="knopf knopf--haupt"
                disabled={!eigenschaftEintragbar}
              >
                Eintragen
              </button>
            </div>
          </form>
        </Abschnitt>

        {/* ==================================================================
            4. SPERRZEITEN (RES-15, TAG-22)
            ================================================================== */}
        <Abschnitt titel="Sperrzeiten">
          <p className="feld__hilfe">
            Ausnahmen haben Vorrang vor der Regel. Eine Sperrzeit im Tagesplan ist nicht
            überschreibbar, und die automatische Zuteilung geht ihr aus dem Weg.{' '}
            <strong>Der Grund ist Pflicht</strong> — „nicht verfügbar" beantwortet nicht, ob man
            fragen kann.
          </p>
          {stand.sperrzeiten.length === 0 ? (
            <p className="hilfe">Keine Sperrzeit eingetragen.</p>
          ) : (
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle" aria-label="Sperrzeiten">
                <thead>
                  <tr>
                    <th scope="col">Art</th>
                    <th scope="col">Grund</th>
                    <th scope="col">Zeitraum</th>
                    <th scope="col">Handlung</th>
                  </tr>
                </thead>
                <tbody>
                  {stand.sperrzeiten.map((s) => (
                    <tr key={s.id} className={s.vorbei ? 'liste__zeile--blass' : undefined}>
                      <td>{sperrart(s.art)}</td>
                      <td>{s.grund}</td>
                      <td>
                        <span className="liste__einzeilig">{zeitpunkt(s.von)}</span> –{' '}
                        <span className="liste__einzeilig">{zeitpunkt(s.bis)}</span>
                        {s.vorbei && <span className="hilfe"> · vorbei</span>}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--eingriff knopf--klein"
                          aria-label={`Sperrzeit ${s.grund} aufheben …`}
                          onClick={() => {
                            setzeNachfrage({ art: 'sperrzeit', zeile: s });
                          }}
                        >
                          Aufheben …
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Dasselbe Formularlayout wie die Stammdaten (Auftrag CRM-73). */}
          <form className="eintragform" onSubmit={(e) => void sperrzeitEintragen(e)}>
            <div className="eintragraster">
              <div className="feld">
                <label htmlFor="konf-sperrart">
                  Art<span className="nur-vorlesen"> der Sperre</span>
                </label>
                <select
                  id="konf-sperrart"
                  value={sperrartWahl}
                  onChange={(e) => {
                    setzeSperrart(e.target.value as Sperrart);
                  }}
                >
                  {sperrartenZurArt(ressource.art).map((a) => (
                    <option key={a} value={a}>
                      {SPERRARTNAME[a]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="feld">
                <label htmlFor="konf-sperrgrund">
                  Grund<span className="nur-vorlesen"> der Sperre</span>{' '}
                  <span className="feld__pflicht">Pflicht</span>
                </label>
                <input
                  id="konf-sperrgrund"
                  value={sperrgrund}
                  onChange={(e) => {
                    setzeSperrgrund(e.target.value);
                  }}
                />
              </div>
              <div className="feld">
                <label htmlFor="konf-sperrvon">Gesperrt von</label>
                <input
                  id="konf-sperrvon"
                  type="datetime-local"
                  value={sperrVon}
                  onChange={(e) => {
                    setzeSperrVon(e.target.value);
                  }}
                />
              </div>
              <div className="feld">
                <label htmlFor="konf-sperrbis">Gesperrt bis</label>
                <input
                  id="konf-sperrbis"
                  type="datetime-local"
                  value={sperrBis}
                  onChange={(e) => {
                    setzeSperrBis(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="knopfreihe">
              <button
                type="submit"
                className="knopf knopf--haupt"
                disabled={!sperrzeitEintragbar}
              >
                Sperrzeit eintragen
              </button>
            </div>
          </form>
        </Abschnitt>

        {/* ==================================================================
            6. ZUGEHÖRIGKEIT UND AUFTRAGSVERARBEITUNG (ÄA-21, RES-39)
            ==================================================================

            Der Auftraggeber am 11.09.2026: „es sind beides trainer, intern und
            extern." Die Unterscheidung steht deshalb hier am Trainer und nicht in
            einer eigenen Ressourcenart.

            DARAN HÄNGT MEHR ALS EIN WORT. Ein externer Trainer verarbeitet Daten
            im Auftrag; RES-39 verlangt dafür eine Vereinbarung, und ohne sie
            bleibt ihm das Tagesbild verschlossen (ÄA-20). Seine eigenen Einsätze
            sieht er weiter — die sind seine eigenen Daten. */}
        {entwurf.zugehoerigkeit !== '' && (
          <Abschnitt titel="Zugehörigkeit und Auftragsverarbeitung">
            <Feldraster>
              <div className="feld">
                <label htmlFor="konf-zugehoerigkeit">Zugehörigkeit</label>
                <select
                  id="konf-zugehoerigkeit"
                  value={entwurf.zugehoerigkeit}
                  onChange={(e) => {
                    const wert = e.target.value as 'intern' | 'extern';
                    setzeEntwurf({
                      ...entwurf,
                      zugehoerigkeit: wert,
                      /* Ein interner trägt keinen Vertragsstatus — die Datenbank
                         weist ihn ab (0181). */
                      auftragsverarbeitung:
                        wert === 'intern'
                          ? 'entfaellt'
                          : entwurf.auftragsverarbeitung === 'entfaellt'
                            ? 'fehlt'
                            : entwurf.auftragsverarbeitung,
                    });
                  }}
                >
                  {(['intern', 'extern'] as const).map((z) => (
                    <option key={z} value={z}>
                      {ZUGEHOERIGKEITSNAME[z] ?? z}
                    </option>
                  ))}
                </select>
                {/* RES-49 */}
                <span className="feld__hilfe">
                  Intern heißt: angestellt, wird in der Trainingsplanung einfach zugeordnet. Extern
                  heißt: arbeitet im Auftrag und wird angefragt.
                </span>
              </div>

                {entwurf.zugehoerigkeit === 'extern' && (
                  <>
                  <div className="feld">
                    <label htmlFor="konf-avv">Auftragsverarbeitungsvereinbarung</label>
                    <select
                      id="konf-avv"
                      value={entwurf.auftragsverarbeitung}
                      onChange={(e) => {
                        setzeEntwurf({
                          ...entwurf,
                          auftragsverarbeitung: e.target
                            .value as 'fehlt' | 'liegt_vor' | 'gekuendigt',
                        });
                      }}
                    >
                      {(['fehlt', 'liegt_vor', 'gekuendigt'] as const).map((v) => (
                        <option key={v} value={v}>
                          {VERTRAGSSTANDNAME[v] ?? v}
                        </option>
                      ))}
                    </select>
                    <span className="feld__hilfe">
                      Ohne vorliegende Vereinbarung gibt das Haus keine personenbezogenen Daten
                      heraus: Der Tagesplan bleibt für ihn zu, seine eigenen Einsätze
                      sieht er.
                    </span>
                  </div>

                  <div className="feld">
                    <label htmlFor="konf-zweck">Zweckbindung</label>
                    <input
                      id="konf-zweck"
                      value={entwurf.zweckbindung}
                      onChange={(e) => {
                        setzeEntwurf({ ...entwurf, zweckbindung: e.target.value });
                      }}
                    />
                    <span className="feld__hilfe">
                      Wozu er die Daten bekommt, im Wortlaut der Vereinbarung — etwa „Durchführung
                      von Fahrsicherheitstrainings".
                    </span>
                  </div>

                  <div className="feld">
                    <label htmlFor="konf-avv-am">Vereinbart am</label>
                    <input
                      id="konf-avv-am"
                      type="date"
                      value={entwurf.vereinbarungAm}
                      onChange={(e) => {
                        setzeEntwurf({ ...entwurf, vereinbarungAm: e.target.value });
                      }}
                    />
                  </div>
                </>
              )}
            </Feldraster>
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf knopf--haupt"
                onClick={() => void stammSpeichern(new Event('submit') as unknown as FormEvent)}
              >
                Speichern
              </button>
            </div>
          </Abschnitt>
        )}

        {/* ==================================================================
            7. DER ONLINEZUGANG (RES-28)
            ==================================================================

            NUR AM TRAINER UND AM DIENSTLEISTER. Ein Schulungsraum bekommt keinen
            Zugang — die Datenbank weist es ab (Migration 0178), und ein Knopf,
            der sicher scheitert, ist ein Knopf zu viel.

            Die Maske fragt Anmeldename und E-Mail, weil die Ressource beides
            nicht hat: Die Kennung ist ein Betriebskuerzel („TR-07") und taugt
            nicht als Anmeldename, und eine Adresse gehoert zur Person hinter der
            Ressource. */}
        {(stand.art === 'trainer' || stand.art === 'dienstleister') && (
          <Abschnitt titel="Onlinezugang">
            {/* RES-31 */}
            <p className="feld__hilfe">
              Der Zugang zeigt ausschließlich die eigenen Einsätze — Datum, Uhrzeit, Dauer, Ort
              und Leistung. Keine Kundendaten, keine Preise, keine Teilnehmer.
            </p>

            {zugangKennwort === null ? (
              <>
                <div className="feld">
                  <label htmlFor="zugang-name">Anmeldename</label>
                  <input
                    id="zugang-name"
                    value={zugangName}
                    onChange={(e) => {
                      setzeZugangName(e.target.value);
                    }}
                  />
                  <span className="feld__hilfe">
                    Buchstaben, Ziffern, Punkt, Bindestrich und Unterstrich. Er wird beim
                    Anmelden eingegeben.
                  </span>
                </div>
                <div className="feld">
                  <label htmlFor="zugang-mail">E-Mail</label>
                  <input
                    id="zugang-mail"
                    type="email"
                    value={zugangMail}
                    onChange={(e) => {
                      setzeZugangMail(e.target.value);
                    }}
                  />
                </div>
                {/*
                  KEINE RÜCKFRAGE (CRM-104): Das Einrichten schickt nichts
                  hinaus, das Kennwort steht gleich darunter, und ein falsch
                  eingerichteter Zugang wird in der Benutzerverwaltung gesperrt
                  (BEN-21). Gefragt wird beim Entziehen, nicht beim Einrichten.
                */}
                <button
                  type="button"
                  className="knopf knopf--klein"
                  disabled={zugangName.trim() === '' || zugangMail.trim() === ''}
                  onClick={() =>
                    void handgriff(async () => {
                      const angelegt = await zugangEinrichten(ressource.id, {
                        anmeldename: zugangName.trim(),
                        email: zugangMail.trim(),
                      });
                      setzeZugangKennwort(angelegt.anfangskennwort);
                      setzeZugangName('');
                      setzeZugangMail('');
                    }, 'Der Zugang ist eingerichtet.')
                  }
                >
                  Zugang einrichten
                </button>
              </>
            ) : (
              /*
               * DAS KENNWORT STEHT EINMAL DA, UND DAS STEHT AUCH DABEI.
               *
               * Es ist nicht wiederherstellbar — gespeichert wird nur sein
               * Streuwert. Wer den Kasten schliesst, ohne es weitergegeben zu
               * haben, setzt es in der Benutzerverwaltung zurueck (BEN-21).
               */
              <div className="streifen streifen--info" role="status">
                <p>
                  Der Zugang ist eingerichtet. Das Anfangskennwort lautet{' '}
                  <strong>{zugangKennwort}</strong>
                </p>
                {/* BEN-06, SYS-05 */}
                <p>
                  Es steht <strong>nur jetzt</strong> hier und lässt sich nicht wieder
                  anzeigen. Geben Sie es persönlich weiter. Beim ersten Anmelden wird es
                  gewechselt, und der zweite Faktor wird eingerichtet.
                </p>
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => {
                    setzeZugangKennwort(null);
                  }}
                >
                  Verstanden
                </button>
              </div>
            )}
          </Abschnitt>
        )}

        {/* ==================================================================
            8. DAS VERTRAGLICHE UND DAS HONORAR (RES-65, RES-86, RES-69)
            ==================================================================

            NUR BEIM EXTERNEN. Ein interner Trainer hat einen Arbeitsvertrag und
            kein Honorar; die Datenbank weist beides an ihm ab (0183, 0185), und
            ein Abschnitt, der sicher scheitert, ist ein Abschnitt zu viel.

            Als eigener Baustein, weil er für genau eine Sorte Ressource gilt und
            eigene Rechte trägt — siehe den Kopf von Trainervertrag.tsx. */}
        {stand.zugehoerigkeit === 'extern' && (
          <Trainervertrag
            ressource={ressource.id}
            darfHonorar={darfHonorar}
            darfHonorarAendern={darfHonorarAendern}
          />
        )}

        {/* 9. DIE ABRECHNUNGSGRUNDLAGE (RES-76, RES-77) — nur mit Honorarrecht. */}
        {stand.zugehoerigkeit === 'extern' && darfHonorar && (
          <Abrechnungsgrundlage
            ressource={ressource.id}
            darfPruefen={darfEingangsrechnungPruefen}
          />
        )}

        {/*
          DIE RÜCKFRAGEN (CRM-104). Eine Eigenschaft und eine Sperrzeit gehen
          hart aus der Datenbank; zurück kommt nur, wer sie neu einträgt — den
          Nachweis und das Ablaufdatum muss man dann wieder zur Hand haben.
        */}
        {nachfrage?.art === 'eigenschaft' && (
          <Rueckfrage
            frage="Eigenschaft entfernen?"
            unterzeile={`${eigenschaftsart(nachfrage.zeile.art)} · ${nachfrage.zeile.schluessel}`}
            tat="Entfernen"
            beiBestaetigen={() =>
              bestaetigt(
                () => eigenschaftEntfernen(ressource.id, nachfrage.zeile.id),
                `${nachfrage.zeile.schluessel} entfernt.`,
              )
            }
            beiSchliessen={() => {
              setzeNachfrage(null);
            }}
          >
            {/* RES-05: Gesucht wird über den Schlüssel. */}
            <p>
              {stand.bezeichnung} deckt danach keinen Bedarf mehr, der „
              {nachfrage.zeile.bezeichnung ?? nachfrage.zeile.schluessel}“ verlangt.
              {nachfrage.zeile.gueltigBis !== null &&
                ` Das Ablaufdatum ${tag(nachfrage.zeile.gueltigBis)} geht mit verloren.`}{' '}
              Zurück kommt sie nur, wenn man sie neu einträgt.
            </p>
          </Rueckfrage>
        )}
        {nachfrage?.art === 'sperrzeit' && (
          <Rueckfrage
            frage="Sperrzeit aufheben?"
            unterzeile={`${sperrart(nachfrage.zeile.art)} · ${zeitpunkt(nachfrage.zeile.von)} – ${zeitpunkt(nachfrage.zeile.bis)}`}
            tat="Aufheben"
            beiBestaetigen={() =>
              bestaetigt(
                () => sperrzeitEntfernen(ressource.id, nachfrage.zeile.id),
                'Die Sperrzeit ist aufgehoben.',
              )
            }
            beiSchliessen={() => {
              setzeNachfrage(null);
            }}
          >
            {/* RES-15, TAG-82: Die automatische Zuteilung geht nur einer Sperrzeit aus dem Weg. */}
            <p>
              {stand.bezeichnung} ist in dieser Zeit wieder verplanbar, und die automatische
              Zuteilung kann sie belegen. Der Grund „{nachfrage.zeile.grund}“ wird gelöscht.
            </p>
          </Rueckfrage>
        )}
      </div>
    </div>
  );
}
